/**
 * `asset-event`: one dated thing that happened to one asset (steward-app#2568, docs/40).
 * A roof replaced, an air conditioner serviced, a fur coat cleaned.
 *
 * The shared half of the type: its closed `kind` set, the field limits and the date rule,
 * kept here so the pack declaration (`index.ts`) and the façade's validation
 * (`services/steward-app-api/src/handlers/assetEvents.ts`) share one copy, and the SPA can
 * import it too. The façade is where they are enforced; the pack declaration repeats the
 * length and enum limits so appcore refuses the same values from any other writer.
 */

/**
 * What happened. Sets the verb the summary uses. Closed on purpose: `appraised` is NOT a kind,
 * because an appraisal is already a `valuation` row and would record the same fact twice
 * (docs/40 §The model).
 */
export const ASSET_EVENT_KINDS = ['installed', 'replaced', 'serviced', 'repaired', 'inspected', 'other'] as const;
export type AssetEventKind = typeof ASSET_EVENT_KINDS[number];

export function isAssetEventKind(v: unknown): v is AssetEventKind {
  return typeof v === 'string' && (ASSET_EVENT_KINDS as readonly string[]).includes(v);
}

export const ASSET_EVENT_TITLE_MAX = 120;
export const ASSET_EVENT_PART_MAX = 80;
export const ASSET_EVENT_NOTES_MAX = 2000;

/**
 * `YYYY-MM` or `YYYY-MM-DD`. A month is the common case ("the roof went on in summer 2014"),
 * and a forced day would invent precision. appcore's `date` field type is `Date.parse`-based
 * and accepts both forms (appcore `packages/core/src/db/zod-compiler.ts`, read 2026-10-26).
 */
export const ASSET_EVENT_DATE_RE = /^\d{4}-\d{2}(-\d{2})?$/;

function daysInMonth(year: number, month: number): number {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** The shape and a real calendar month (and day, when given). Says nothing about the future. */
export function isRealAssetEventDate(date: string): boolean {
  if (!ASSET_EVENT_DATE_RE.test(date)) return false;
  const [y, m, d] = date.split('-').map(Number) as [number, number, number | undefined];
  if (m < 1 || m > 12) return false;
  if (d === undefined) return true;
  return d >= 1 && d <= daysInMonth(y, m);
}

/**
 * Is `date` no later than `now` plus one day? The day of grace is for time zones: the
 * family's today can be UTC's tomorrow. Compared as strings, which is exact for both forms:
 * a month `YYYY-MM` sorts before every day inside it, so the current month is allowed and the
 * next one is not until its first day is within the grace.
 */
export function isAssetEventDateNotFuture(date: string, now: Date = new Date()): boolean {
  const limit = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return date <= limit;
}
